import { IconFont, Tooltip } from '@byecode/ui'
import type { BaseViewField, DTFile, HighLightConditions } from '@lighthouse/core'
import { divide, subtract } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { FILE_ICON_MAP } from '../../constants/common'
import { useApplicationContext } from '../../contexts'
import { getFileNameByUrl, getFileTypeByUrl, getImageFullUrlInApplication, getOSSThumbnailImageUrl } from '../../utils'
import { FilePreviewer } from '../FilePreviewer'
import { CellHighlighter } from '../Highlighter'

// import type { FieldValuePreviewerProps } from './types'

interface FileCellPreviewerProps {
    data?: DTFile[]
    viewField?: BaseViewField
    isWrap?: boolean
    width?: number
    styleConditions?: HighLightConditions
    itemClickable?: boolean
}

const SCxFileList = styled.div<{ isWrap: boolean }>`
    position: relative;
    max-width: 100%;
    display: flex;
    flex-wrap: ${({ isWrap }) => (isWrap ? 'wrap' : 'nowrap')};
    /* &.flex-nowrap,
    .flex-nowrap {
        flex-wrap: nowrap;
        flex-shrink: 0;
        margin-bottom: 0;
    } */
`

const ImageFile = styled.div`
    width: 24px;
    height: 24px;
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    border-radius: 2px;
    background-color: var(--color-gray-100);
    cursor: zoom-in;
`

const SCxFileWrapper = styled.div`
    min-width: 24px;
    height: 24px;
    margin-right: 6px;
    /* margin-bottom: 6px; */
    overflow: hidden;

    &:nth-last-of-type(1) {
        margin-right: 0;
    }
`

const SCxOtherFile = styled.span`
    display: flex;
    align-items: center;
    height: 24px;
    padding: 0 2px;
    max-width: 100%;
    overflow: hidden;
    border-radius: 4px;
    background-color: var(--color-gray-100);
`

const SCxFileIcon = styled(IconFont)`
    font-size: 20px;
    cursor: zoom-in;
`

export const FileValuePreviewer: React.FC<FileCellPreviewerProps> = ({
    viewField,
    data = [],
    isWrap = true,
    width = 160,
    styleConditions,
    itemClickable = true
}) => {
    const [opened, setOpened] = useState(false)
    const [defaultIndex, setDefaultIndex] = useState(0)
    const { appId } = useApplicationContext()
    const showLength = useMemo(() => Math.floor(divide(width, 30)) - 1, [width])

    const extraLength = useMemo(() => subtract(data.length, showLength), [data.length, showLength])

    const fileList = useMemo(
        () =>
            data.map(item => {
                const name = getFileNameByUrl(item) || ''
                const type = getFileTypeByUrl(item)
                return {
                    name,
                    url: getImageFullUrlInApplication(appId, item),
                    type
                }
            }),
        [appId, data]
    )

    const renderFile = useCallback(
        (fileUrl: string, openable = false) => {
            const fileName = getFileNameByUrl(fileUrl) || ''
            const fileType = getFileTypeByUrl(fileUrl)
            return fileType === 'image' ? (
                <ImageFile
                    style={{
                        backgroundImage: `url("${getOSSThumbnailImageUrl(getImageFullUrlInApplication(appId, fileUrl), {
                            width: 24,
                            height: 24,
                            quality: 50
                        })}")`
                    }}
                />
            ) : (
                <Tooltip withArrow title={fileName} placement="top">
                    <SCxOtherFile>
                        {FILE_ICON_MAP[fileType] ? (
                            <SCxFileIcon type={FILE_ICON_MAP[fileType]} />
                        ) : (
                            <SCxFileIcon type={FILE_ICON_MAP.other} />
                        )}
                    </SCxOtherFile>
                </Tooltip>
            )
        },
        [appId]
    )

    return (
        <>
            {viewField && <CellHighlighter fieldId={viewField.fieldId} type="date" value={data.join(',')} conditions={styleConditions} />}
            <SCxFileList
                isWrap={isWrap}
                onClick={ev => {
                    if (itemClickable) {
                        ev.stopPropagation()
                    }
                }}
                onMouseDown={ev => {
                    if (itemClickable) {
                        ev.stopPropagation()
                    }
                }}
            >
                {data.slice(0, showLength).map((fileUrl, i) => (
                    <SCxFileWrapper
                        key={fileUrl}
                        style={{ cursor: itemClickable ? 'pointer' : 'default' }}
                        onClick={ev => {
                            setOpened(true)
                            setDefaultIndex(i)
                        }}
                    >
                        {renderFile(fileUrl, itemClickable)}
                    </SCxFileWrapper>
                ))}
                {extraLength > 0 && (
                    <SCxFileWrapper>
                        <SCxOtherFile>+{extraLength}</SCxOtherFile>
                    </SCxFileWrapper>
                )}
                <FilePreviewer defaultIndex={defaultIndex} fileList={fileList} opened={opened} onClose={() => setOpened(false)} />
            </SCxFileList>
        </>
    )
}
